import React, { useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  TableHead,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  collection,
  arrayRemove,
} from 'firebase/firestore';

import { collections } from '../../constants/defines';
import { findLocationDataByLocation } from '../../db/productLocation';
import { EnhancedTableHead } from '../Page/table';
import SearchProdInInventory from '../Page/searchProdInInventory';
import {
  addInventoryLog,
  addLocationLogForInventory,
} from '../../db/inventory';

const headCells = [
  {
    id: 'location',
    numeric: false,
    align: 'left',
    enableSort: false,
    disablePadding: false,
    label: 'Location',
    width: '100%',
  },
  {
    id: 'actions',
    numeric: false,
    align: 'right',
    enableSort: false,
    disablePadding: false,
  },
];

const RenderLocationList = ({
  classes,
  inventoryLocations,
  handleRequestSort,
  db,
  setShowToast,
  setToastData,
}) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('location');
  const [expandedRows, setExpandedRows] = useState({});
  const [rowData, setRowData] = useState({}); // Separate state for each row's data
  const [editingRows, setEditingRows] = useState({}); // Track editing state for each row
  const [addingRows, setAddingRows] = useState({}); // Track "Add Product" state
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const unsubscribeRefs = useRef({});

  const getUser = () => {
    const user = localStorage.getItem('currentuser');
    return user ? user : 'N/A';
  };

  const handleToggleRow = (index, location) => {
    if (expandedRows[index]) {
      // Collapse row and unsubscribe
      if (unsubscribeRefs.current[index]) {
        unsubscribeRefs.current[index]();
        delete unsubscribeRefs.current[index];
      }
      setExpandedRows((prev) => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    } else {
      setExpandedRows((prev) => ({
        ...prev,
        [index]: true,
      }));
      // Expand row and fetch data
      findLocationDataByLocation(
        db,
        location,
        (data) => {
          setRowData((prev) => ({
            ...prev,
            [index]: data, // Store data specific to this row
          }));
        },
        (unsubscribe) => {
          unsubscribeRefs.current[index] = unsubscribe; // Save the unsubscribe function
        },
      );
    }
  };

  const handleSave = async (item, updatedName, updatedQuantity) => {
    try {
      const itemId = item.id;
      const docRef = doc(db, collections.INVENTORY, itemId);

      const itemUpdated = {
        name: updatedName,
        quantity: updatedQuantity,
        location: item.location,
        parent: item.parent,
        sku: item.sku,
      };

      await updateDoc(docRef, {
        ...itemUpdated,
        updatedAt: new Date(),
        updatedBy: getUser(),
      });

      addInventoryLog(db, itemUpdated, 'Inventory Updated');

      // Update local state and exit editing mode
      setRowData((prev) => {
        const updatedRowData = { ...prev };
        Object.keys(updatedRowData).forEach((key) => {
          updatedRowData[key] = updatedRowData[key].map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  name: updatedName,
                  quantity: updatedQuantity,
                }
              : item,
          );
        });
        return updatedRowData;
      });

      setEditingRows((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    } catch (error) {
      console.error('Error updating document:', error);
    }
  };

  const [itemToDelete, setItemToDelete] = useState(null);
  const [open, setOpen] = useState(false);

  const handleOpenModal = (item) => {
    setItemToDelete(item);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!itemToDelete) {
      return;
    }

    try {
      const docRef = doc(db, collections.INVENTORY, itemToDelete.id);
      await deleteDoc(docRef);
      addInventoryLog(db, itemToDelete, 'Inventory Deleted');

      // Update local state to remove the deleted row
      setRowData((prev) => {
        const updatedRowData = { ...prev };
        Object.keys(updatedRowData).forEach((key) => {
          updatedRowData[key] = updatedRowData[key].filter(
            (item) => item.id !== itemToDelete,
          );
        });
        return updatedRowData;
      });

      // Close the modal after successful deletion
      handleCloseModal();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleLocationDelete = async (location) => {
    try {
      const docRef = doc(db, collections.INVENTORY_LOCATIONS, 'locations');

      await updateDoc(docRef, {
        locations: arrayRemove(location), // Removes the specific location
      });

      addLocationLogForInventory(db, location, 'Location Deleted');

      console.log(`Location ${location} deleted successfully.`);
    } catch (error) {
      console.error('Error deleting location:', error);
    }
  };

  const deleteLocation = async () => {
    const { location, index } = selectedData;
    if (!location || index === undefined) {
      return;
    }

    // only delete if no products in the location
    findLocationDataByLocation(
      db,
      location,
      async (data) => {
        if (data.length === 0) {
          await handleLocationDelete(location);
          setShowToast(true);
          setToastData({
            message: `Location ${location} deleted successfully.`,
            isError: false,
          });
          handleCloseRemoveModal();
        } else {
          setShowToast(true);
          setToastData({
            message: `Location: ${location} has products. Cannot delete.`,
            isError: true,
          });
          handleCloseRemoveModal();
        }
      },
      (unsubscribe) => {
        unsubscribeRefs.current[index] = unsubscribe; // Save the unsubscribe function
      },
    );
  };

  const handleAddProduct = (location) => {
    setAddingRows((prev) => ({
      ...prev,
      [location]: { name: '', sku: '', quantity: 0, location: location },
    }));
  };

  const handleCancelAddProduct = (location) => {
    setAddingRows((prev) => {
      const newState = { ...prev };
      delete newState[location];
      return newState;
    });
  };

  const handleSaveNewProduct = async (location) => {
    try {
      const newProduct = addingRows[location];
      const dataConstruct = {
        sku: newProduct.sku.trim(),
        name: newProduct.name.trim(),
        quantity: parseInt(newProduct.quantity),
        location: location?.toUpperCase(),
        parent: newProduct.parent ?? '',
        createdAt: new Date(),
        createdBy: getUser(),
      };

      addInventoryLog(db, dataConstruct, 'New Inventory Created');

      // if something is missing, don't save
      if (!dataConstruct.sku || !dataConstruct.quantity) {
        alert('Please fill in all fields');
        return;
      }

      // check if quantity is a number && greater than 0 not negative
      if (isNaN(dataConstruct.quantity) || dataConstruct.quantity < 0) {
        alert('Quantity must be a positive number');
        return;
      }

      addDoc(collection(db, collections.INVENTORY), dataConstruct)
        .then((doc) => {
          console.log('New Inventory created');
          console.log(doc.id);
        })
        .catch((error) => {
          console.error('Error adding Inventory: ', error);
        });

      // Update local state with the new product
      setRowData((prev) => {
        const updatedRowData = { ...prev };
        if (updatedRowData[location]) {
          updatedRowData[location].push(newProduct);
        } else {
          updatedRowData[location] = [newProduct];
        }
        return updatedRowData;
      });

      setAddingRows((prev) => {
        const newState = { ...prev };
        delete newState[location];
        return newState;
      });
    } catch (error) {
      console.error('Error saving new product:', error);
    }
  };

  const handleSelectProduct = (newValue, location) => {
    setAddingRows((prev) => ({
      ...prev,
      [location]: {
        ...prev[location],
        name: newValue.name,
        sku: newValue.sku,
      },
    }));
  };

  const handleCloseRemoveModal = () => {
    setOpenRemoveModal(false);
    setSelectedData({});
  };

  const handleOpenRemoveModal = (location, index) => {
    setSelectedData({ location, index });
    setOpenRemoveModal(true);
  };

  return (
    <TableContainer sx={{ marginTop: '30px', marginBottom: '50px' }}>
      <Table
        className={classes.table}
        aria-labelledby="tableTitle"
        size="medium"
        aria-label="enhanced table"
      >
        <EnhancedTableHead
          classes={classes}
          useCheckbox={false}
          cells={headCells}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
        <TableBody>
          {inventoryLocations.map((location, index) => (
            <React.Fragment key={`location-row-${index}`}>
              <TableRow
                hover
                tabIndex={-1}
                onClick={() => handleToggleRow(index, location)}
                classes={{
                  root: classes.tableRow2,
                }}
              >
                <TableCell align="left">
                  <IconButton size="small">
                    {expandedRows[index] ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                  <Typography variant="tableText">{location}</Typography>
                </TableCell>
                <TableCell align="right">
                  {/* prevent default */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents the row from toggling
                      handleOpenRemoveModal(location, index);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              {/* Collapsible Content */}
              <TableRow>
                <TableCell
                  style={{ paddingBottom: 0, paddingTop: 0 }}
                  colSpan={headCells.length}
                >
                  <Collapse
                    in={expandedRows[index]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <div style={{ padding: '10px 20px' }}>
                      {rowData[index] ? (
                        <TableContainer>
                          <Table size="small" aria-label="items table">
                            <TableHead>
                              <TableRow>
                                <TableCell align="left">
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                  >
                                    Name
                                  </Typography>
                                </TableCell>
                                <TableCell align="left">
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                  >
                                    SKU
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                  >
                                    Quantity
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rowData[index].map((item) => {
                                const isRowEditing = editingRows[item.id];
                                return (
                                  <TableRow key={`item-${index}-${item.id}`}>
                                    <TableCell align="left">
                                      <Typography variant="body2">
                                        {item.name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                      <Typography variant="body2">
                                        {item.sku}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {isRowEditing ? (
                                        <TextField
                                          value={isRowEditing.quantity}
                                          onChange={(e) =>
                                            setEditingRows((prev) => ({
                                              ...prev,
                                              [item.id]: {
                                                ...prev[item.id],
                                                quantity: e.target.value,
                                              },
                                            }))
                                          }
                                          type="number"
                                          variant="outlined"
                                          size="small"
                                        />
                                      ) : (
                                        <Typography variant="body2">
                                          {item.quantity}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ display: 'flex', gap: '10px' }}
                                    >
                                      {isRowEditing ? (
                                        <>
                                          {/* cancel edit */}
                                          <IconButton
                                            onClick={() =>
                                              setEditingRows((prev) => ({
                                                ...prev,
                                                [item.id]: null,
                                              }))
                                            }
                                          >
                                            <CancelIcon />
                                          </IconButton>
                                          {/* save edit */}
                                          <IconButton
                                            onClick={() =>
                                              handleSave(
                                                item,
                                                isRowEditing.name,
                                                isRowEditing.quantity,
                                              )
                                            }
                                          >
                                            <SaveIcon />
                                          </IconButton>
                                        </>
                                      ) : (
                                        <Button
                                          variant="outlined"
                                          onClick={() =>
                                            setEditingRows((prev) => ({
                                              ...prev,
                                              [item.id]: {
                                                name: item.name,
                                                quantity: item.quantity,
                                                id: item.id,
                                              },
                                            }))
                                          }
                                        >
                                          Edit
                                        </Button>
                                      )}
                                      {!isRowEditing && (
                                        <IconButton
                                          onClick={() => handleOpenModal(item)}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {!addingRows[location] && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    marginTop: '10px',
                                  }}
                                >
                                  <Button
                                    variant="outlined"
                                    onClick={() => {
                                      handleAddProduct(location);
                                    }}
                                  >
                                    Add Product
                                  </Button>
                                </Box>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2">
                          <LinearProgress />
                        </Typography>
                      )}
                      {/* Add Product Row */}
                      {addingRows[location] && (
                        <TableRow sx={{ width: '100%' }}>
                          <TableCell align="left" sx={{ width: '60%' }}>
                            <SearchProdInInventory
                              db={db}
                              onSelect={handleSelectProduct}
                              location={location}
                              singlesInventoryBypass={true}
                              sx={{ width: '100%' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              value={addingRows[location].quantity}
                              onChange={(e) =>
                                setAddingRows((prev) => ({
                                  ...prev,
                                  [location]: {
                                    ...prev[location],
                                    quantity: e.target.value,
                                  },
                                }))
                              }
                              type="number"
                              placeholder="Enter Quantity"
                              variant="outlined"
                              size="small"
                              sx={{ width: '100%' }}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ display: 'flex', gap: '10px', width: '100%' }}
                          >
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => handleCancelAddProduct(location)}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => handleSaveNewProduct(location)}
                            >
                              Save
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Add Product Button */}
                    </div>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}

          {/* Confirmation Modal */}
          <Dialog open={open} onClose={handleCloseModal}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this item? This action cannot be
                undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseModal}
                color="primary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button onClick={handleDelete} color="error" variant="outlined">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={openRemoveModal} onClose={handleCloseRemoveModal}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this location? This action
                cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseRemoveModal}
                color="primary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button onClick={deleteLocation} color="error" variant="outlined">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {!inventoryLocations.length && (
            <TableRow>
              <TableCell align="center" colSpan={headCells.length}>
                <Typography>No locations found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RenderLocationList;
